import type React from "react"
import { createContext, useContext, useState, useEffect } from "react"
import { Question } from "@/types"
import { RootState } from "@/store"
import { useSelector } from "react-redux"

interface QuestionsContextType {
  loading: boolean
  questions: Question[]
  initialized: boolean
  addQuestions: (newQuestions: Question[]) => void
  updateQuestion: (id: string, updatedQuestion: Question) => void
  clearQuestions: () => void
  importQuestions: (importedQuestions: Question[]) => void
}

const QuestionsContext = createContext<QuestionsContextType | undefined>(undefined)

export function useQuestions() {
  const context = useContext(QuestionsContext)
  if (context === undefined) {
    throw new Error("useQuestions must be used within a QuestionsProvider")
  }
  return context
}

export function QuestionsProvider({ children }: { children: React.ReactNode }) {
  const { questions: prevQuestions, initialized, loading } = useSelector((state: RootState) => state.questionBank)
  const [questions, setQuestions] = useState<Question[]>([])

  useEffect(() => {
    if (initialized) {
      setQuestions(prevQuestions as any)
    }
  }, [prevQuestions, initialized])

  const addQuestions = (newQuestions: Question[]) => {
    const mergedMap = new Map<string | number, Question>()

    questions.forEach(q => mergedMap.set(q.id, q))
    newQuestions.forEach(q => mergedMap.set(q.id, q))

    setQuestions(Array.from(mergedMap.values()))
  }

  const updateQuestion = (id: string, updatedQuestion: Question) => {
    setQuestions((prev) => prev.map((q) => (q.id === id ? updatedQuestion : q)))
  }

  const clearQuestions = () => {
    setQuestions([])
  }

  const importQuestions = (importedQuestions: Question[]) => {
    setQuestions((prev) => [...prev, ...importedQuestions])
  }

  const value = {
    loading,
    questions,
    initialized,
    addQuestions,
    updateQuestion,
    clearQuestions,
    importQuestions,
  }

  return <QuestionsContext.Provider value={value}>{children}</QuestionsContext.Provider>
}
